import { defineComponent as _defineComponent } from 'vue'
import InvoiceSection from '@/views/my-projects/components/invoice/InvoiceSection.vue';
import InvoiceAddressDataDisplay from '@/views/my-projects/components/invoice/addressdata/InvoiceAddressDataDisplay.vue';
import InvoiceAddressDataEditor from '@/views/my-projects/components/invoice/addressdata/InvoiceAddressDataEditor.vue';
import { computed, ref, watch } from 'vue';
import useInvoice from '@/views/my-projects/composables/useInvoice';
import { cloneDeep } from 'lodash';
import { PayloadUpdateInvoice, ValidateInputs, InvoiceAddressData } from '@/models/Invoice';
import useUser from '@/mixins/useUser';


export default /*#__PURE__*/_defineComponent({
  __name: 'InvoiceAddressData',
  setup(__props) {

const {
  componentAddressDataSender,
  componentAddressDataReceiver,
  fetchUpdateInvoice,
  canInvoiceBeEdited,
  errorMessagesAddressDataReceiver,
  errorMessagesAddressDataSender,
  unsavedAreas,
  saveUnsavedChanges
} = useInvoice();
const { tUserFallback: t } = useUser('self');

const localDataSender = ref(cloneDeep(componentAddressDataSender.value));
const localDataReceiver = ref(cloneDeep(componentAddressDataReceiver.value));
const invoiceAddressDataEditorSenderRef = ref<ValidateInputs | null>(null);
const invoiceAddressDataEditorReceiverRef = ref<ValidateInputs | null>(null);
const isAddressDataEditing = computed({
  get: () => unsavedAreas.value.addressData.isUnsaved,
  set: (val: boolean) => {
    unsavedAreas.value.addressData.isUnsaved = val;
  }
});

/**
 * Trims whitespace from all string fields in local sender and receiver data.
 */
function trimStringFieldsOfLocalData() {
  [localDataSender.value, localDataReceiver.value].forEach((obj) => {
    (Object.keys(obj) as (keyof typeof obj)[]).forEach((key) => {
      if (typeof obj[key] === 'string') obj[key] = obj[key].trim();
    });
  });
}

/**
 * Creates the payload for updating an invoice by removing the zipAndCity field
 * from both sender and receiver address data.
 *
 * @returns The payload object containing sender and receiver address data.
 */
function createPayload(): PayloadUpdateInvoice {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { zipAndCity, ...dataSender } = localDataSender.value;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { zipAndCity: _, ...dataReceiver } = localDataReceiver.value;

  return {
    sender: {
      ...dataSender,
      salutation: dataSender.salutation === 'NONE' ? dataSender.salutation : `${t(`invoice.address-data.salutationOptions.${dataSender.salutation}`)}`
    } as InvoiceAddressData,
    receiver: {
      ...dataReceiver,
      salutation: dataReceiver.salutation === 'NONE' ? dataReceiver.salutation : `${t(`invoice.address-data.salutationOptions.${dataReceiver.salutation}`)}`
    } as InvoiceAddressData
  };
}

/**
 * Asynchronously validates sender and receiver inputs.
 * If both are valid, updates the invoice and sets the new address data.
 */
const saveCallback = async () => {
  trimStringFieldsOfLocalData();

  try {
    const invalidReceiver = await invoiceAddressDataEditorReceiverRef.value?.validateInputs();
    const invalidSender = await invoiceAddressDataEditorSenderRef.value?.validateInputs();
    const areInputsValid = invalidSender && invalidReceiver; // The result must be waited for individually, otherwise the validation within the second function is not called, because if the first is false, the second would not be executed.
    if (!areInputsValid) return;

    const payload: PayloadUpdateInvoice = createPayload();
    await fetchUpdateInvoice(0, payload)
      .then(() => {
        componentAddressDataSender.value = localDataSender.value;
        componentAddressDataReceiver.value = localDataReceiver.value;
        isAddressDataEditing.value = false;
      });
  } finally {
    unsavedAreas.value.addressData.isProcessing = false;
  }
};

/**
 * Resets the local address data to the current global address data, discarding any unsaved changes when canceling.
 */
function onCancel() {
  localDataSender.value = cloneDeep(componentAddressDataSender.value);
  localDataReceiver.value = cloneDeep(componentAddressDataReceiver.value);
}

// If reactive data changes, it is updated
watch(
  () => componentAddressDataSender.value,
  () => {
    if (!isAddressDataEditing.value) {
      localDataSender.value = cloneDeep(componentAddressDataSender.value);
      localDataReceiver.value = cloneDeep(componentAddressDataReceiver.value);
    }
  }
);

watch(
  () => saveUnsavedChanges.value,
  (newVal) => {
    if (newVal === 'save' && unsavedAreas.value.addressData.isUnsaved) {
      unsavedAreas.value.addressData.isProcessing = true;
      saveCallback();
    } else unsavedAreas.value.addressData.isProcessing = false;
  }
);

return { __sfc: true,componentAddressDataSender, componentAddressDataReceiver, fetchUpdateInvoice, canInvoiceBeEdited, errorMessagesAddressDataReceiver, errorMessagesAddressDataSender, unsavedAreas, saveUnsavedChanges, t, localDataSender, localDataReceiver, invoiceAddressDataEditorSenderRef, invoiceAddressDataEditorReceiverRef, isAddressDataEditing, trimStringFieldsOfLocalData, createPayload, saveCallback, onCancel, InvoiceSection, InvoiceAddressDataDisplay, InvoiceAddressDataEditor }
}

})